import {z} from 'zod'
import jwt from 'jsonwebtoken'
import {uri} from '../uri'
import {DateTime as D, type DateTime} from 'luxon'
import {ok} from '../promise'
import {apiClient} from '../apiClient'
import {pick} from 'remeda'
import {zx} from '../type'
import {WorkspaceTypes} from './workspaceTypes'
import {fmtNum} from '../format'

/** this is in shared rather than forecast because the host CLI needs to fetch license as well */

export const stripeLicensePlans = ['starter', 'team'] as const
export type StripeLicensePlan = (typeof stripeLicensePlans)[number]
export const licensePlans = ['trial', ...stripeLicensePlans, 'enterprise'] as const
export type LicensePlan = (typeof licensePlans)[number]

/** the license stored for workspace */
export const zLicense = z.object({
  /** if false, the workspace is not restricted by a license */
  enabled: z.boolean().default(false),
  plan: z.enum(licensePlans).default('trial'),
  users: z.number().optional(),
  participants: z.number().optional(),
  expires: z.string().optional(),
  notes: z.string().optional(),
  selfHost: z.boolean().optional(),
  updated: z.string().optional(),
  /** the date this was was synced from stripe */
  updatedStripe: z.string().optional()
})
export type License = z.infer<typeof zLicense>

export const zAuthTokens = z.object({
  sb_url: z.string(),
  access_token: z.string(),
  refresh_token: z.string()
})
export type AuthToken = z.infer<typeof zAuthTokens>

/** a license token used for licensing self hosted version of the app */
export const zLicenseToken = z.object({
  workspace_id: z.string(),
  workspace_name: z.string(),
  for_email: z.string().optional(),
  ...pick(zLicense.shape, ['plan', 'expires', 'selfHost'])
})
export type LicenseToken = z.infer<typeof zLicenseToken>

export const currencySymbols = {
  usd: '$',
  eur: '€',
  gbp: '£',
  aud: 'A$'
} as const
export const zCurrency = zx.enumFromObjKeys(currencySymbols)

export const zUsage = z.object({
  users_now: z.number(),
  users_max_since_last_billing: z.number(),
  participants_since_last_billing: z.number(),
  last_billing_date: zx.nullish.string
})

export const zInvoiceItem = z.object({
  id: z.string(),
  number: z.string(),
  amount: z.number(),
  currency: zCurrency,
  status: z.string(),
  created: z.number(),
  invoice_pdf: z.string().nullish(),
  hosted_invoice_url: z.string().nullish(),
  lines: z
    .array(
      z.object({
        description: z.string(),
        amount: z.coerce.number(),
        unitPrice: z.coerce.number().nullish(),
        qty: z.number()
      })
    )
    .optional()
})

export const licenseApiMd = {
  getLicenses: {
    zReq: z.object({workspace_id: z.string().optional()}),
    zResOk: z.object({tokens: z.array(z.string())})
  },
  getPlans: {
    zReq: z.object({}),
    zResOk: z.object({
      plans: z.array(
        z.object({
          name: z.enum(stripeLicensePlans),
          price: z.number(),
          priceUsers: z.number(),
          priceParticipants: z.number(),
          currency: zCurrency,
          includedUsers: z.number(),
          includedParticipants: z.number()
        })
      )
    })
  },
  getInvoices: {
    zReq: z.object({workspace_id: z.string()}),
    zResOk: z.object({
      invoices: z.array(zInvoiceItem)
    })
  },
  createCheckoutSession: {
    zReq: z.object({
      workspace_id: z.string(),
      plan: z.enum(stripeLicensePlans),
      returnParams: z.record(z.string(), z.string())
    }),
    zResOk: z.object({
      clientSecret: z.string()
    })
  },
  updateSubscription: {
    zReq: z.object({workspace_id: z.string(), plan: z.enum(stripeLicensePlans)}),
    zResOk: z.object({})
  },
  sessionStatus: {
    zReq: z.object({workspace_id: z.string(), sessionId: z.string()}),
    zResOk: z.object({sessionStatus: z.enum(['no_payment_required', 'paid', 'unpaid'])})
  },
  cancelSubscription: {
    zReq: z.object({workspace_id: z.string(), reactivate: z.boolean().optional()})
  },
  stripeSync: {
    zReq: z.object({syncType: z.enum(['recent', 'all', 'usage'])}),
    zResOk: z.object({
      updatedWorkspaces: z.array(z.object({id: z.string(), name: z.string()})),
      syncedFrom: z.string().optional()
    })
  },
  stripeEventTest: {
    zReq: z.object({workspace_id: z.string(), profile_id: z.string()})
  },
  getUsage: {
    zReq: z.object({workspace_id: z.string()}),
    zResOk: zUsage
  },
  mondaySync: {
    zReq: z.object({})
  }
} as const

export const licenseApi = apiClient({md: licenseApiMd, url: uri('/api/license')})

export type GetLicensesRequest = z.infer<typeof licenseApiMd.getLicenses.zReq>
export type GetLicensesResult = z.infer<typeof licenseApiMd.getLicenses.zResOk>

export type GetPlansResult = z.infer<typeof licenseApiMd.getPlans.zResOk>
export const getPlans = () =>
  licenseApi.post('getPlans', {}).then(r => {
    if (!r.ok) return r
    const plans = r.plans.map(p => ({
      ...p,
      currencySymbol: currencySymbols[p.currency]
    }))
    return ok({plans})
  })
export type Plan = Extract<Awaited<ReturnType<typeof getPlans>>, {ok: true}>['plans'][number]
export type PlanCurrency = Plan['currency']

export const fetchLicenses = ({
  appUrl,
  accessToken,
  workspace_id,
  licenseKeyPublic,
  isSelfHost
}: {
  appUrl?: string
  accessToken?: string
  workspace_id?: string
  licenseKeyPublic?: string
  isSelfHost: boolean | undefined
}) =>
  apiClient({
    md: licenseApiMd,
    url: uri(`${appUrl ?? ''}/api/license`) // use a client like this because this could be called from a CLI or on the web
  })
    .post('getLicenses', {workspace_id}, {headers: accessToken ? {Authorization: `Bearer ${accessToken}`} : undefined})
    .then(r =>
      !r.ok ? r : (
        ok({
          licenses: r.tokens.map(token => ({
            token,
            ...decodeLicenseToken(token, licenseKeyPublic),
            expired: isLicenseExpired({
              ...decodeLicenseToken(token, licenseKeyPublic),
              isSelfHost
            })
          }))
        })
      )
    )

export const decodeLicenseToken = (token: string, publicKey?: string): LicenseToken => {
  try {
    const decoded = publicKey ? jwt.verify(token, publicKey) : jwt.decode(token)
    return zLicenseToken.parse(decoded)
  } catch (e) {
    throw new Error(`Failed to verify license token: ${e}`)
  }
}

export const isLicenseExpired = ({
  expires,
  plan,
  isSelfHost
}: Pick<LicenseToken, 'expires' | 'plan'> & {isSelfHost?: boolean}) =>
  expires != null && (plan == 'trial' || isSelfHost) && D.fromISO(expires) < D.now()

export type StripeSyncResult = z.infer<typeof licenseApiMd.stripeSync.zResOk>

export const trialLicenseCfg = ({workspaceCreatedAt}: {workspaceCreatedAt: DateTime}) =>
  ({
    enabled: true,
    plan: 'trial',
    expires: workspaceCreatedAt.plus({days: 14}).toISO()!,
    participants: 10,
    users: 2
  }) satisfies License

export const fmtCurrency = (price: number, options?: {currency: PlanCurrency; currencySymbol?: string}) => {
  const symbol = options?.currencySymbol ?? (options?.currency ? currencySymbols[options.currency] : '')
  return `${symbol}${fmtNum(price / 100, '0[.]00')}`
}
